import { useId, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Alert } from './ui/Alert';
import { Sheet, SheetBody, SheetContent, SheetFooter } from './ui/Sheet';
import { apiFetch } from '../lib/api';
import {
  ApiError,
  type Capability,
  type CustomRole,
  type CustomRolesResponse,
  type TenantUser,
} from '../types/api';

/** What each capability means, in the words the admin sees. */
export const CAPABILITY_LABEL: Record<Capability, string> = {
  'campaigns.edit': 'Create and edit campaigns',
  'campaigns.test_send': 'Send test emails',
  'campaigns.send': 'Prepare sends and A/B winners',
  'templates.edit': 'Edit templates and shared blocks',
  'contacts.edit': 'Edit contacts and suppressions',
  'forms.edit': 'Edit signup forms, popup and landing pages',
  'ai.use': 'Use the AI assistant',
  'settings.edit': 'Edit catalog, labels and other settings',
};

const ALL_CAPABILITIES = Object.keys(CAPABILITY_LABEL) as Capability[];

const BASE_LABEL = { editor: 'Editor', viewer: 'Viewer' } as const;

function roleErrorText(err: unknown): string {
  if (err instanceof ApiError && err.status === 409 && err.body.error === 'duplicate_name') {
    return 'A role with that name already exists.';
  }
  if (err instanceof ApiError && err.status === 409 && err.body.error === 'limit') {
    return 'This account has the maximum number of roles.';
  }
  if (err instanceof ApiError && err.status === 409 && err.body.error === 'in_use') {
    return 'Members still have this role. Give them another role first.';
  }
  if (err instanceof ApiError && err.status === 400) return 'Check the name and try again.';
  if (err instanceof ApiError && err.status === 403) return 'Only administrators can manage roles.';
  return 'The role could not be saved. Try again.';
}

export function useCustomRoles(enabled: boolean) {
  return useQuery({
    queryKey: ['custom-roles'],
    queryFn: () => apiFetch<CustomRolesResponse>('/custom-roles'),
    enabled,
  });
}

/**
 * Create or edit one role. The checkboxes show what the role is ALLOWED to
 * do; an unticked box is a denied capability. A viewer base can never
 * edit, whatever is ticked, so the boxes only matter for an editor base.
 */
function RoleSheet({
  open,
  onOpenChange,
  role,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  role: CustomRole | null;
}) {
  const [name, setName] = useState(role?.name ?? '');
  const [baseRole, setBaseRole] = useState<'editor' | 'viewer'>(role?.baseRole ?? 'editor');
  const [denied, setDenied] = useState<Capability[]>(role?.denied ?? []);
  const queryClient = useQueryClient();
  const nameId = useId();
  const baseId = useId();

  const save = useMutation({
    mutationFn: () =>
      apiFetch(role ? `/custom-roles/${role.id}` : '/custom-roles', {
        method: role ? 'PUT' : 'POST',
        body: { name: name.trim(), baseRole, denied },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['custom-roles'] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onOpenChange(false);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch(`/custom-roles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['custom-roles'] });
      onOpenChange(false);
    },
  });

  function toggle(cap: Capability, allowed: boolean) {
    setDenied((d) => (allowed ? d.filter((c) => c !== cap) : [...d, cap]));
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim().length > 0 && !save.isPending) save.mutate();
  }

  const error = save.error ?? remove.error;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {open ? (
        <SheetContent
          eyebrow="Roles"
          title={role ? `Edit role: ${role.name}` : 'New role'}
          description="A role starts from Editor or Viewer and can only take things away. It never gives more than its base role."
        >
          <form id="custom-role-form" onSubmit={submit}>
            <SheetBody>
              {error ? <Alert>{roleErrorText(error)}</Alert> : null}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={nameId}>Name</Label>
                <Input
                  id={nameId}
                  value={name}
                  maxLength={40}
                  autoComplete="off"
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={baseId}>Based on</Label>
                <select
                  id={baseId}
                  className="h-[42px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
                  value={baseRole}
                  onChange={(e) => setBaseRole(e.target.value as 'editor' | 'viewer')}
                >
                  <option value="editor">{BASE_LABEL.editor}</option>
                  <option value="viewer">{BASE_LABEL.viewer}</option>
                </select>
              </div>
              <fieldset className="flex flex-col gap-2 border-0 p-0 m-0">
                <legend className="text-sm font-medium mb-1">Allowed</legend>
                {baseRole === 'viewer' ? (
                  <p className="m-0 text-sm text-muted">
                    Viewers can only look. Choose Editor to allow any of these.
                  </p>
                ) : (
                  ALL_CAPABILITIES.map((cap) => (
                    <label key={cap} className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={!denied.includes(cap)}
                        onChange={(e) => toggle(cap, e.target.checked)}
                      />
                      {CAPABILITY_LABEL[cap]}
                    </label>
                  ))
                )}
              </fieldset>
            </SheetBody>
          </form>
          <SheetFooter>
            {role ? (
              <Button
                type="button"
                variant="danger"
                disabled={remove.isPending || role.memberCount > 0}
                title={role.memberCount > 0 ? 'Give its members another role first' : undefined}
                onClick={() => remove.mutate(role.id)}
              >
                Delete
              </Button>
            ) : null}
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="custom-role-form"
              disabled={name.trim().length === 0 || save.isPending}
            >
              {save.isPending ? 'Saving…' : 'Save role'}
            </Button>
          </SheetFooter>
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

function summary(role: CustomRole): string {
  if (role.baseRole === 'viewer') return 'View only';
  if (role.denied.length === 0) return 'Everything an editor can do';
  return `Cannot: ${role.denied.map((c) => lowerFirst(CAPABILITY_LABEL[c])).join(', ')}`;
}

/** Settings → Users → Roles (K8). Admin-only. */
export function CustomRolesSection() {
  const { data, isLoading, isError } = useCustomRoles(true);
  const [editing, setEditing] = useState<CustomRole | 'new' | null>(null);
  const roles = data?.roles ?? [];
  const atLimit = data ? roles.length >= data.maxRoles : false;

  return (
    <Card className="p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold m-0">Roles</h2>
        <Button size="sm" variant="secondary" disabled={atLimit} onClick={() => setEditing('new')}>
          New role
        </Button>
      </div>
      <p className="m-0 text-sm text-muted">
        Give a member less than Editor or Viewer allows, for example editing without sending tests.
        Members without a role keep their normal access.
      </p>
      {isLoading ? <div className="text-muted text-sm">Loading roles…</div> : null}
      {isError ? <Alert>The roles could not be loaded.</Alert> : null}
      {data && roles.length === 0 ? <div className="text-muted text-sm">No roles yet.</div> : null}
      {roles.length > 0 ? (
        <ul className="m-0 p-0 list-none flex flex-col">
          {roles.map((r) => (
            <li
              key={r.id}
              className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-border last:border-0"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">
                  {r.name}{' '}
                  <span className="text-muted font-normal">· {BASE_LABEL[r.baseRole]}</span>
                </div>
                <div className="text-xs text-muted">
                  {summary(r)} · {r.memberCount} {r.memberCount === 1 ? 'member' : 'members'}
                </div>
              </div>
              <Button
                size="sm"
                variant="secondary"
                aria-label={`Edit role: ${r.name}`}
                onClick={() => setEditing(r)}
              >
                Edit
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {editing !== null ? (
        <RoleSheet
          key={editing === 'new' ? 'new' : editing.id}
          open
          onOpenChange={(next) => {
            if (!next) setEditing(null);
          }}
          role={editing === 'new' ? null : editing}
        />
      ) : null}
    </Card>
  );
}

/** The per-member role picker in the members table. Admins cannot get a role. */
export function MemberRoleSelect({ member, roles }: { member: TenantUser; roles: CustomRole[] }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (customRoleId: string | null) =>
      apiFetch(`/users/${member.id}/custom-role`, { method: 'PUT', body: { customRoleId } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['custom-roles'] });
    },
  });
  if (member.role === 'admin') return <span className="text-xs text-muted">Full access</span>;
  return (
    <div className="flex flex-col gap-1">
      <select
        aria-label={`Custom role: ${member.email}`}
        className="h-[34px] max-w-[180px] rounded-[9px] border border-zinc-300 bg-card px-2 text-sm"
        value={member.customRoleId ?? ''}
        disabled={mutation.isPending}
        onChange={(e) => mutation.mutate(e.target.value === '' ? null : e.target.value)}
      >
        <option value="">None</option>
        {roles.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </select>
      {mutation.isError ? (
        <span className="text-xs text-danger-text">{roleErrorText(mutation.error)}</span>
      ) : null}
    </div>
  );
}
